import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import CheckIcon from "@material-ui/icons/Check";
import React from "react";
import "./SearchableMultiSelect.scss";

export interface SearchableMultiSelectOptionText {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
}

interface Props<T> {
  id: string;
  label: string;
  options: T[];
  value: string[];
  onChange: (values: string[]) => void;
  getOptionValue: (option: T) => string;
  getOptionLabel?: (option: T) => React.ReactNode;
  renderOption?: (option: T) => SearchableMultiSelectOptionText;
  renderValue?: (values: string[]) => React.ReactNode;
  noneSelectedText?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  emptyText?: React.ReactNode;
  menuWidth?: number;
  descriptionMaxChars?: number;
  className?: string;
}

export const renderSelectedSummary = (values: string[]) => {
  if (values.length === 0) {
    return "";
  }
  if (values.length >= 3) {
    return `Selected (${values.length})`;
  }
  return values.join(", ");
};

export function SearchableMultiSelect<T>(props: Props<T>) {
  const {
    id,
    label,
    options,
    value,
    onChange,
    getOptionValue,
    getOptionLabel,
    renderOption,
    renderValue,
    noneSelectedText = "No selection",
    searchValue,
    onSearchChange,
    searchPlaceholder,
    loading = false,
    loadingMore = false,
    hasMore = false,
    onLoadMore,
    emptyText,
    menuWidth = 480,
    descriptionMaxChars,
    className,
  } = props;

  const truncateDescription = (secondary: React.ReactNode) => {
    if (
      typeof secondary !== "string" ||
      !descriptionMaxChars ||
      secondary.length <= descriptionMaxChars
    ) {
      return secondary;
    }
    return `${secondary.slice(0, descriptionMaxChars)}…`;
  };

  const handleMenuScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    if (!onLoadMore || !hasMore || loadingMore) {
      return;
    }
    if (el.scrollHeight - el.scrollTop - el.clientHeight >= 60) {
      return;
    }
    onLoadMore();
  };

  return (
    <FormControl variant="filled" className={className}>
      <InputLabel id={`${id}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        multiple
        value={value}
        onChange={(event) => onChange(event.target.value as string[])}
        renderValue={(selected: unknown) =>
          renderValue
            ? renderValue(selected as string[])
            : value.length === 0
            ? noneSelectedText
            : renderSelectedSummary(value)
        }
        MenuProps={{
          // focus belongs to the search box, not the list items
          autoFocus: false,
          // anchor to the select's bottom so the popup always opens
          // downward — MUI's default aligns the selected item over the
          // select and pushes the paper up instead
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          getContentAnchorEl: null,
          PaperProps: {
            style: {
              maxHeight: 400,
              width: menuWidth,
              maxWidth: "90vw",
            },
            onScroll: handleMenuScroll,
          },
          MenuListProps: {
            disablePadding: true,
            className: "sms-menu-list",
            // the search box only makes sense for long, remote option lists
            subheader: onSearchChange ? (
              <ListSubheader className="sms-search-header">
                <TextField
                  autoFocus
                  fullWidth
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    // keep keys out of the menu, Escape still closes it
                    if (e.key !== "Escape") {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
            ) : undefined,
          },
        }}
      >
        {options.length === 0 && (
          <MenuItem disabled value="">
            <em>
              {loading
                ? "Loading..."
                : emptyText !== undefined
                ? emptyText
                : "No options"}
            </em>
          </MenuItem>
        )}
        {options.map((option) => {
          const optionValue = getOptionValue(option);
          const text: SearchableMultiSelectOptionText = renderOption
            ? renderOption(option)
            : getOptionLabel
            ? { primary: getOptionLabel(option) }
            : { primary: optionValue };
          return (
            <MenuItem key={optionValue} value={optionValue}>
              <ListItemText
                primary={text.primary}
                secondary={truncateDescription(text.secondary)}
                secondaryTypographyProps={
                  text.secondary ? { noWrap: true } : undefined
                }
              />
              {value.includes(optionValue) && <CheckIcon fontSize="small" />}
            </MenuItem>
          );
        })}
        {loadingMore && options.length > 0 && (
          <MenuItem disabled value="">
            <em>Loading more…</em>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
